// Tier 0 + Tier 1 — Affordability calculation engine.
// Pure functions, no React dependencies. All inputs are simple numbers.
// All outputs are deterministic for the same set of inputs.

// ── Tier 0 primitives ──────────────────────────────────────────────
export function emi(P, annualRatePct, years) {
    if (P <= 0 || years <= 0) return 0;
    const r = (annualRatePct / 100) / 12;
    const n = Math.round(years * 12);
    if (r === 0) return P / n;
    const pow = Math.pow(1 + r, n);
    return (P * r * pow) / (pow - 1);
}

export function loanFor(targetEMI, annualRatePct, years) {
    if (targetEMI <= 0 || years <= 0) return 0;
    const r = (annualRatePct / 100) / 12;
    const n = Math.round(years * 12);
    if (r === 0) return targetEMI * n;
    const pow = Math.pow(1 + r, n);
    return targetEMI * ((pow - 1) / (r * pow));
}

export function compound(base, ratePct, years) {
    return base * Math.pow(1 + ratePct / 100, years);
}

function emiFactor(annualRatePct, years) {
    return emi(1, annualRatePct, years); // EMI required per ₹1 of loan
}

// ── Tier 1 · Affordability composite calculation ───────────────────
export function computeAffordability(inputs) {
    const {
        salary = 0,
        obligations = 0,
        bankRule = 55,
        downPayment = 0,
        tenure = 20,
        rate = 8.5,
        targetEMI = 50000,
    } = inputs || {};

    // D1 · monthly rate
    const monthlyRate = (rate / 100) / 12;
    // D2 · EMI per ₹1 of loan
    const factor = emiFactor(rate, tenure);
    // D3 · maximum allowable EMI by FOIR rule
    const max_allowable_emi = Math.max(0, (salary * bankRule) / 100 - obligations);
    // D4 · eligible loan
    const eligible_loan = factor > 0 ? max_allowable_emi / factor : 0;
    // D5 · EMI on that loan
    const emi_on_eligible_loan = eligible_loan * factor;
    // D6 · maximum affordable property
    const max_affordable_property = eligible_loan + downPayment;
    // D7 · total repayment over tenure
    const total_repayment = emi_on_eligible_loan * tenure * 12;
    // D8 · total interest
    const total_interest = Math.max(0, total_repayment - eligible_loan);
    // D9 · interest ratio
    const interest_ratio = total_repayment > 0 ? total_interest / total_repayment : 0;

    // D10 · obligation burden
    const obligation_burden_pct = salary > 0 ? (obligations / salary) * 100 : 0;
    // D11 · FOIR usage at eligible loan
    const foir_usage_pct = salary > 0
        ? ((emi_on_eligible_loan + obligations) / salary) * 100
        : 0;

    // D12 · FOIR status (3-band)
    let foir_status = "Acceptable";
    if (foir_usage_pct > bankRule + 5) foir_status = "Critical";
    else if (foir_usage_pct > bankRule) foir_status = "High";

    // D13 · segment classification
    let segment = "Entry";
    if (max_affordable_property >= 20000000) segment = "Premium";
    else if (max_affordable_property >= 10000000) segment = "Aspirational";
    else if (max_affordable_property >= 4000000) segment = "Mid Market";

    // D14 · affordability alert
    const downRatio = eligible_loan > 0 ? downPayment / eligible_loan : 1;
    const is_affordability_alert =
        foir_status !== "Acceptable" ||
        downRatio < 0.20 ||
        obligation_burden_pct > 30;

    // D15 · stamp duty + registration + cess (Bengaluru apartment: 5% + 1% + 1.65% = 7.65%)
    const registration_cost = max_affordable_property * 0.0765;
    // D16 · interior buffer (5%)
    const interior_buffer = max_affordable_property * 0.05;
    // D17 · total upfront cash
    const total_upfront_cash = downPayment + registration_cost + interior_buffer;

    // D18 · FOIR comparison rows at 50 / 55 / 60 / 65
    const foir_comparison_rows = [50, 55, 60, 65].map((rule) => {
        const emi_allow = Math.max(0, (salary * rule) / 100 - obligations);
        const loan = factor > 0 ? emi_allow / factor : 0;
        const home = loan + downPayment;
        const usage = salary > 0 ? ((emi_allow + obligations) / salary) * 100 : 0;
        let status;
        if (usage <= rule - 2) status = "conservative";
        else if (usage <= rule + 2) status = "balanced";
        else if (usage <= rule + 7) status = "stretched";
        else status = "risky";
        return { rule, emi: emi_allow, home, loan, status };
    });

    // D19 · target-EMI inverse
    const target_loan = loanFor(targetEMI, rate, tenure);
    const target_home_value = target_loan + downPayment;
    let years_saved = 0;
    if (targetEMI > emi_on_eligible_loan && eligible_loan > 0) {
        const r = monthlyRate;
        if (r > 0) {
            const x = 1 - (r * eligible_loan) / targetEMI;
            if (x > 0) {
                const newMonths = -Math.log(x) / Math.log(1 + r);
                years_saved = Math.max(0, tenure - newMonths / 12);
            }
        } else {
            years_saved = Math.max(0, tenure - eligible_loan / (targetEMI * 12));
        }
    }

    // S_aff · affordability sub-score (60 base + components, cap 100)
    const noAlert = !is_affordability_alert;
    const downGood = eligible_loan > 0 && downRatio >= 0.20;
    const segmentGood = segment !== "Entry";
    const affordability_score = Math.min(
        100,
        60 + (noAlert ? 20 : 0) + (downGood ? 10 : 0) + (segmentGood ? 10 : 0)
    );

    return {
        // raw echoes
        salary, obligations, bankRule, downPayment, tenure, rate, targetEMI,
        // Tier 1 derivations
        monthlyRate,
        emiFactor: factor,
        max_allowable_emi,
        eligible_loan,
        emi_on_eligible_loan,
        max_affordable_property,
        total_repayment,
        total_interest,
        interest_ratio,
        obligation_burden_pct,
        foir_usage_pct,
        foir_status,
        segment,
        is_affordability_alert,
        registration_cost,
        interior_buffer,
        total_upfront_cash,
        foir_comparison_rows,
        target_loan,
        target_home_value,
        years_saved,
        affordability_score,
    };
}
